'use client';

import { ReactNode } from 'react';
import { Globe, LayoutGrid, FileText, Settings } from 'lucide-react';

interface DemoShellProps {
  children: ReactNode;
  currentView: 'projects' | 'spec';
  projectName?: string;
  specName?: string;
}

export function DemoShell({ children, currentView, projectName, specName }: DemoShellProps) {
  // Build breadcrumb URL based on current view
  const getUrl = () => {
    if (currentView === 'projects') {
      return 'specledger.io/projects';
    }
    return `specledger.io/projects/${projectName}/specs/${specName}`;
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      {/* Browser Chrome */}
      <div className="bg-[#2d2d2d] rounded-t-xl border border-[#404040] overflow-hidden shadow-2xl">
        {/* Title Bar */}
        <div className="flex items-center gap-2 px-4 py-3 bg-[#252525] border-b border-[#404040]">
          {/* Window Controls */}
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
            <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
            <div className="w-3 h-3 rounded-full bg-[#28c840]" />
          </div>

          {/* Tab */}
          <div className="flex-1 flex justify-center">
            <div className="flex items-center gap-2 px-4 py-1.5 bg-[#2d2d2d] rounded-lg text-sm">
              <Globe className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300">SpecLedger</span>
            </div>
          </div>

          {/* Spacer for symmetry */}
          <div className="w-14" />
        </div>

        {/* URL Bar */}
        <div className="flex items-center gap-3 px-4 py-2 bg-[#2d2d2d] border-b border-[#404040]">
          <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-[#1a1a1a] rounded-lg">
            <div className="flex items-center gap-1 text-green-500">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
              </svg>
            </div>
            <span className="text-gray-400 text-sm font-mono">{getUrl()}</span>
          </div>
        </div>

        {/* App Navigation */}
        <div className="flex items-center gap-1 px-4 py-2 bg-[#252525] border-b border-[#404040]">
          <NavItem icon={<LayoutGrid className="w-4 h-4" />} label="Projects" active={currentView === 'projects'} />
          <NavItem icon={<FileText className="w-4 h-4" />} label="Docs" />
          <NavItem icon={<Settings className="w-4 h-4" />} label="Settings" />
        </div>
      </div>

      {/* Content Area */}
      <div className="bg-bg-alt border-x border-b border-[#404040] rounded-b-xl min-h-[500px] overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function NavItem({ icon, label, active = false }: { icon: ReactNode; label: string; active?: boolean }) {
  return (
    <button
      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
        active
          ? 'bg-primary/20 text-primary'
          : 'text-gray-400 hover:text-gray-300 hover:bg-white/5'
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
